// Times as people read them (owner 2026-10-08: "These times need to be
// localized for the user viewing them"). One place, so every page writes a
// time alike:
//   - an absolute time is the viewer's own -- the browser's zone,
//     Intl.DateTimeFormat().resolvedOptions().timeZone -- with a short zone
//     name: "Oct 8, 2026 at 11:20 AM MDT" (dateTimeText);
//   - its hover is the precise UTC instant, "2026-10-08 17:20:05 UTC"
//     (utcText), the same for every reader;
//   - relative times stay as they are ("just now", formatAgo), with the
//     UTC instant as their hover.
// Every function takes the zone (default: the viewer's), so a page can use
// the zone a response was counted in, and a test can pin one.
//
// Counts by hour or day are made on the server, in the zone the admin
// sends (tz=: content's timezone package), never by shifting UTC buckets
// here: an hour bucket isn't a local hour across a daylight-saving change
// or at +05:30. A response names the zone it counted in (timeZone); a page
// labels and computes its days in that zone (dayOf, dayWindow).
//
// This module imports nothing (filters-model loads it, and its
// time-zone test runs both in a child Node process).

export const UTC = 'UTC'

const UTC_NAMES = ['UTC', 'Etc/UTC', 'UCT', 'Etc/UCT', 'Universal', 'Etc/Universal', 'Zulu', 'Etc/Zulu', 'GMT', 'Etc/GMT', 'GMT0', 'Etc/GMT0',
  'GMT+0', 'Etc/GMT+0', 'GMT-0', 'Etc/GMT-0', 'Greenwich', 'Etc/Greenwich']
const isUTC = tz => !tz || UTC_NAMES.includes(tz)

// A zone tests pin (jsdom can't switch the process's own); null: the
// browser's.
let pinned = null
export const pinViewerTimeZone = tz => {
  pinned = tz || null
}

// The browser's zone (UTC when it can't say).
export const viewerTimeZone = () => {
  if (pinned) return pinned
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || UTC
  } catch (e) {
    return UTC
  }
}

// Formatters are costly to build: one per zone and options. A zone this
// browser doesn't know is read as UTC (and named UTC: zoneOf).
const formatters = new Map()
const known = new Map()
const zoneOf = tz => {
  if (isUTC(tz)) return UTC
  if (!known.has(tz)) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz }) // eslint-disable-line no-new
      known.set(tz, true)
    } catch (e) {
      known.set(tz, false)
    }
  }
  return known.get(tz) ? tz : UTC
}
const formatter = (tz, opts) => {
  const key = `${tz}|${JSON.stringify(opts)}`
  if (!formatters.has(key)) formatters.set(key, new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts }))
  return formatters.get(key)
}
const partsOf = (ms, tz, opts) => {
  const out = {}
  formatter(tz, opts).formatToParts(ms).forEach(p => { out[p.type] = p.value })
  return out
}

const msOf = v => {
  if (v === null || v === undefined || v === '') return NaN
  if (v instanceof Date) return v.getTime()
  if (typeof v === 'number') return v
  return Date.parse(v)
}
const pad = (n, w = 2) => String(n).padStart(w, '0')
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const CLOCK = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23', weekday: 'short' }

// The wall clock in tz at an instant: { year, month (1-12), day, hour
// (0-23), minute, second, weekday (Monday 0) }; null for no time.
export const wallClock = (value, tz = viewerTimeZone()) => {
  const ms = msOf(value)
  if (!Number.isFinite(ms)) return null
  const p = partsOf(ms, zoneOf(tz), CLOCK)
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour: Number(p.hour) % 24,
    minute: Number(p.minute),
    second: Number(p.second),
    weekday: WEEKDAYS.indexOf(p.weekday)
  }
}

// "MDT", "MST", "GMT+5:30", "UTC": the zone's short name at that instant.
export const zoneAbbr = (value = Date.now(), tz = viewerTimeZone()) => {
  const zone = zoneOf(tz)
  const ms = msOf(value)
  if (zone === UTC || !Number.isFinite(ms)) return UTC
  return partsOf(ms, zone, { timeZoneName: 'short' }).timeZoneName || zone
}

// "Mountain Time", "India Standard Time", "UTC": the zone by a name that
// holds all year, for a count over many days (which may cross a
// daylight-saving change); its short name where it has no such name.
export const zoneName = (tz = viewerTimeZone()) => {
  const zone = zoneOf(tz)
  if (zone === UTC) return UTC
  for (const style of ['longGeneric', 'long']) {
    try {
      const name = partsOf(Date.now(), zone, { timeZoneName: style }).timeZoneName
      if (name && !/^GMT([+-]|$)/.test(name)) return name
    } catch (e) {
      // a browser without this style: try the next
    }
  }
  return zoneAbbr(Date.now(), zone)
}

const DATE = { month: 'short', day: 'numeric', year: 'numeric' }

// "Oct 8, 2026": the day in tz holding the instant; '' for no time.
export const dateText = (value, { tz = viewerTimeZone(), year = true } = {}) => {
  const ms = msOf(value)
  if (!Number.isFinite(ms)) return ''
  return formatter(zoneOf(tz), { ...DATE, year: year ? 'numeric' : undefined }).format(ms)
}

// "11:20 AM MDT" ("11:20:05 AM MDT" with seconds; no zone with zone:
// false); '' for no time.
export const timeText = (value, { tz = viewerTimeZone(), seconds = false, zone = true } = {}) => {
  const ms = msOf(value)
  if (!Number.isFinite(ms)) return ''
  const t = formatter(zoneOf(tz), { hour: 'numeric', minute: '2-digit', second: seconds ? '2-digit' : undefined }).format(ms).replace(/\s/g, ' ')
  return zone ? `${t} ${zoneAbbr(ms, tz)}` : t
}

// "Oct 8, 2026 at 11:20 AM MDT": an absolute time as the page shows one;
// '' for no time.
export const dateTimeText = (value, { tz = viewerTimeZone(), seconds = false, year = true } = {}) => {
  const ms = msOf(value)
  if (!Number.isFinite(ms)) return ''
  return `${dateText(ms, { tz, year })} at ${timeText(ms, { tz, seconds })}`
}

// "2026-10-08 17:20:05 UTC" ("…05.250 UTC" when it has milliseconds): the
// precise instant, the same for every reader -- an absolute or relative
// time's hover. '' for no time.
export const utcText = value => {
  const ms = msOf(value)
  if (!Number.isFinite(ms)) return ''
  const s = new Date(ms).toISOString()
  return `${s.slice(0, 10)} ${s.slice(11, ms % 1000 ? 23 : 19)} UTC`
}

/* ---- days ---- */

// "2026-10-08": the day in tz holding the instant; '' for no time.
export const dayOf = (value, tz = viewerTimeZone()) => {
  const c = wallClock(value, tz)
  return c ? `${c.year}-${pad(c.month)}-${pad(c.day)}` : ''
}

const DAY = 864e5
const dayParts = day => day.split('-').map(Number)

// The calendar day n days after day (YYYY-MM-DD; zone-free).
export const addDays = (day, n) => {
  const [y, m, d] = dayParts(day)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

// tz's offset from UTC at an instant, in ms (Denver in summer: -6 h).
const offsetAt = (ms, tz) => {
  const c = wallClock(ms, tz)
  return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) - Math.floor(ms / 1000) * 1000
}

// The first instant of a day (YYYY-MM-DD) in tz, in ms: its local midnight;
// where the clocks skipped midnight, the instant they jumped (as Postgres's
// date_trunc('day', t, tz) reads it). Tried with the offsets either side
// of the day: the earliest that falls on it.
export const startOfDay = (day, tz = viewerTimeZone()) => {
  const zone = zoneOf(tz)
  const [y, m, d] = dayParts(day)
  const wall = Date.UTC(y, m - 1, d)
  const tries = [wall - DAY, wall, wall + DAY].map(t => wall - offsetAt(t, zone)).filter(t => dayOf(t, zone) === day)
  return tries.length ? Math.min(...tries) : wall - offsetAt(wall, zone)
}

// One day in tz as the instants the API reads: { from, to } (to exclusive:
// the next day's start), 23 or 25 hours across a daylight-saving change.
export const dayWindow = (day, tz = viewerTimeZone()) => ({
  from: new Date(startOfDay(day, tz)).toISOString(),
  to: new Date(startOfDay(addDays(day, 1), tz)).toISOString()
})

// The days (YYYY-MM-DD, in order) a window from..to (instants; to
// exclusive) touches in tz.
export const daysBetween = (from, to, tz = viewerTimeZone()) => {
  const a = msOf(from)
  const b = msOf(to)
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return []
  const last = dayOf(b - 1, tz)
  const out = []
  for (let day = dayOf(a, tz); day <= last && out.length < 1000; day = addDays(day, 1)) out.push(day)
  return out
}

/* ---- how long ago ---- */

// How long ago an instant was, as a course card says it (owner
// 2026-10-09): "just now" (under a minute, or ahead of the clock), "1
// minute ago" / "N minutes ago", "N hours ago" (under a day), "yesterday"
// (the calendar day before, in tz), "N days ago" (2 to 6 calendar days),
// then the day: "Sep 28", "Sep 28, 2025" in another year (in tz). Days are
// counted on the viewer's calendar, so "yesterday" is the date before, not
// "24 to 48 hours ago", and the count never says "7 days ago". now is the
// page's clock (use-minute-clock.js keeps one). '' for no time, and for
// Go's unset time.Time (0001-01-01).
export const agoText = (value, { now = Date.now(), tz = viewerTimeZone() } = {}) => {
  const ms = msOf(value)
  if (!Number.isFinite(ms) || ms < 0) return ''
  const diff = now - ms
  if (diff < 60e3) return 'just now'
  if (diff < 36e5) return plural(Math.floor(diff / 60e3), 'minute')
  if (diff < DAY) return plural(Math.floor(diff / 36e5), 'hour')
  const days = calendarDaysBetween(ms, now, tz)
  if (days <= 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  const sameYear = wallClock(ms, tz).year === wallClock(now, tz).year
  return dateText(ms, { tz, year: !sameYear })
}

const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'} ago`

// Calendar days in tz from the day holding a to the day holding b.
const calendarDaysBetween = (a, b, tz) => {
  const [ya, ma, da] = dayParts(dayOf(a, tz))
  const [yb, mb, db] = dayParts(dayOf(b, tz))
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / DAY)
}
